import { useCallback } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { useRestaurantsService } from '@restworld/data-services';
import { Restaurant } from '@restworld/utility-types';

import { activeRestaurantOnboardingStateAtom } from '.';
import { activeAddressIdAtom, activeRestaurantAtom } from './google-address-input-result';

export const useRestaurantInformationOnboardingQueryKey = () => {
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  return [`restaurant-onboarding-data-${activeAddressId}-${activeRestaurant}`];
};
export const restaurantInformationDataAtom = atom<Partial<Restaurant>>({});

const useRestaurantInformationOnboardingDataQuery = () => {
  const restaurantServices = useRestaurantsService();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  const setActiveRestaurant = useSetAtom(activeRestaurantAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const queryKey = useRestaurantInformationOnboardingQueryKey();

  const queryFn = useCallback(async () => {
    const data = (
      await restaurantServices.getAdditionalRestaurantOnboardingData({
        restaurant: activeRestaurant || undefined,
        gmaps_place_id: activeAddressId,
      })
    ).data;
    setRestaurantInformationData(data);
    setActiveUI('restaurant_onboarding_form');

    // When restaurant name wasn't provided (URL param flow), derive from API response
    // so it's available for form payload formatting (business_address field)
    if (!activeRestaurant && data?.restaurant_name) {
      const name = data.restaurant_name as string;
      const addr = (data.address as string) || '';
      setActiveRestaurant(addr ? `${name}, ${addr}` : name);
    }

    return data;
  }, [
    activeAddressId,
    activeRestaurant,
    restaurantServices,
    setActiveUI,
    setActiveRestaurant,
    setRestaurantInformationData,
  ]);

  const query = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    retry(failureCount, error) {
      console.error(`Error fetching restaurant onboarding data: ${error}`);
      if (error?.['code'] === 409) {
        setActiveUI('restaurant_already_exists');
        return false;
      }
      if (error?.['code'] === 400 && error?.['data']?.error === 'not_horeca') {
        setActiveUI('not_horeca');
        return false;
      }
      return failureCount < 5; // Retry up to 5 times
    },
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled: !!activeAddressId,
  });

  return query;
};

export const useRestaurantOnboardingData = () => {
  const restaurantInformationData = useAtomValue(restaurantInformationDataAtom);
  return restaurantInformationData;
};

export default useRestaurantInformationOnboardingDataQuery;
